import { ReactNode, useRef } from 'react';

import Flex from '@decisiv/ui-components/lib/components/Flex';
import Tooltip from '@decisiv/ui-components/lib/components/Tooltip';

import fleetAdminDeniedReasonText from 'utils/User/fleetAdminDeniedReason';

interface FleetAdminActionProps {
  capability: FleetAdminCapability;
  children: (ctx: { disabled: boolean }) => ReactNode;
}

/**
 * Wraps a Fleet Admin control (Button / MenuItem / IconButton) so that
 * the control is rendered disabled when the BE-provided `capability`
 * denies it, with a tooltip showing the translated denial reason. The
 * render prop receives `{ disabled }` and the caller decides how to
 * express it (e.g. forward to the underlying component's `disabled`
 * prop).
 *
 * The BE returns `capability.deniedReason` as a symbolic enum; this
 * component owns the translation into user-facing copy.
 */
export default function FleetAdminAction({
  capability,
  children,
}: FleetAdminActionProps) {
  const targetRef = useRef<HTMLDivElement>(null);
  const disabled = !capability.allowed;

  return (
    <>
      {disabled && (
        <Tooltip target={targetRef}>
          {fleetAdminDeniedReasonText(capability.deniedReason)}
        </Tooltip>
      )}
      <Flex ref={targetRef}>{children({ disabled })}</Flex>
    </>
  );
}
